import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ArrowUp2, ArrowDown2, CloseCircle } from "iconsax-react";
import { authSend } from "./routingApi";

/**
 * Create/edit a ring group. The member list is ordered — that order is exactly the order "Ring in
 * order" walks through — so selected agents are shown as a reorderable list rather than a flat
 * multi-select blob where order is invisible.
 */
export default function RingGroupDialog({ open, onClose, onSaved, teamMembers, teams, editingGroup }) {
  const [name, setName] = useState("");
  const [memberIds, setMemberIds] = useState([]);
  const [teamIds, setTeamIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(editingGroup?.name || "");
    setMemberIds(editingGroup?.memberIds?.map((m) => m._id) || []);
    setTeamIds(editingGroup?.teamIds?.map((t) => t._id) || []);
  }, [editingGroup, open]);

  const nameOf = (id) => teamMembers.find((m) => m._id === id)?.name || "Unknown agent";
  const move = (index, delta) => {
    const next = [...memberIds];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setMemberIds(next);
  };

  const handleSave = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (memberIds.length === 0 && teamIds.length === 0) return toast.error("Pick at least one agent or team");
    setSaving(true);
    try {
      if (editingGroup) await authSend("put", `/api/calling/ring-groups/${editingGroup._id}`, { name, memberIds, teamIds });
      else await authSend("post", "/api/calling/ring-groups", { name, memberIds, teamIds });
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save ring group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingGroup ? "Edit ring group" : "New ring group"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <TextField label="Name" size="small" value={name} onChange={(e) => setName(e.target.value)} autoFocus />

        <Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.5 }}>Agents</Typography>
          <Select
            multiple
            displayEmpty
            size="small"
            fullWidth
            value={memberIds}
            onChange={(e) => setMemberIds(e.target.value)}
            renderValue={(selected) => (selected.length ? `${selected.length} selected` : "Add agents…")}
          >
            {teamMembers.map((m) => (
              <MenuItem key={m._id} value={m._id}>
                {m.name}
              </MenuItem>
            ))}
          </Select>

          {memberIds.length > 0 && (
            <Box component="ol" sx={{ listStyle: "none", m: 0, mt: 1, p: 0, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1.5 }}>
              {memberIds.map((id, i) => (
                <Box
                  component="li"
                  key={id}
                  sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.5, borderTop: i === 0 ? "none" : (t) => `1px solid ${t.palette.divider}` }}
                >
                  <Typography sx={{ width: 20, fontSize: 12.5, fontWeight: 700, color: "text.secondary" }}>{i + 1}</Typography>
                  <Typography sx={{ flex: 1, fontSize: 14 }} noWrap>
                    {nameOf(id)}
                  </Typography>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${nameOf(id)} up`}>
                        <ArrowUp2 size={16} color="currentColor" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton size="small" disabled={i === memberIds.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${nameOf(id)} down`}>
                        <ArrowDown2 size={16} color="currentColor" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => setMemberIds(memberIds.filter((x) => x !== id))} aria-label={`Remove ${nameOf(id)}`}>
                      <CloseCircle size={16} color="currentColor" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.75 }}>
            Order matters for “Ring in order” — the top agent rings first. Teams below ring after these agents.
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.5 }}>Teams</Typography>
          <Select
            multiple
            displayEmpty
            size="small"
            fullWidth
            value={teamIds}
            onChange={(e) => setTeamIds(e.target.value)}
            renderValue={(selected) =>
              selected.length ? selected.map((id) => teams.find((t) => t._id === id)?.teamName).filter(Boolean).join(", ") : "Add teams…"
            }
          >
            {teams.map((t) => (
              <MenuItem key={t._id} value={t._id}>
                {t.teamName}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
